import { Context } from 'access-control/Context.enum';
import { hasPermissionInContext } from 'access-control/PermissionChecker';
import { UserPickerItemDataType } from 'atomic';
import { PhoneNumberFormat } from 'google-libphonenumber';
import { UNASSIGNED_OPTION_VALUE } from 'lib/constants';
import { CountryCode } from 'lib/enum/country-code.enum';
import { CountryList } from 'lib/enum/country.enum';
import { Permission } from 'lib/enum/permission.enum';
import { TalentSource } from 'lib/enum/talent-source.enum';
import { createAssigneeRoles } from 'lib/utils/assigneRoles.utils';
import { Iri, generateIri, getUuidFromIri, getUuidFromNullableItem } from 'lib/utils/iri';
import { KomboIntegrationStatus } from 'services/Api/Kombo/types';
import {
    BusinessAccountMembership,
    CurrentUser,
    KomboIntegration,
    Talent,
    TalentEntry,
    TalentGateway,
    TalentsConversationsUnreadCountsByUser,
    Talentpool,
    TalentStatus,
    TalentStatusTheme,
    User,
    UserProductItemRole,
    UserProductRole,
    withErrorHandler,
} from 'services';
import * as Yup from 'yup';
import { phoneUtil, validatePhoneNumber } from './phone.utils';
import { getUserDisplayName } from './user.utils';
import { ResourceKind } from 'lib/enum/resource-kind.enum';
import { Option } from 'rsuite/esm/internals/types';
import { NavigateFunction } from 'react-router';

const PHONE_NUMBER_MAX_LENGTH = 1000;
const EMAIL_MAX_LENGTH = 1000;
const EMAIL_TO_LONG_MESSAGE = `Email must be at most ${EMAIL_MAX_LENGTH} characters length`;

export const getTalentAssignees = (
    memberships: BusinessAccountMembership[],
    usersDict: Record<string, User>,
    userProductRoles: UserProductRole[],
    userProductItemRoles: UserProductItemRole[],
): User[] =>
    memberships
        .filter(membership => {
            const foundUserProductRole = userProductRoles.find(({ user: userIri }) => userIri === membership.user);
            const foundUserProductItemRole = userProductItemRoles.find(
                ({ user: userIri }) => userIri === membership.user,
            );
            const assigneeRoles = createAssigneeRoles(membership!, foundUserProductRole, foundUserProductItemRole);

            return hasPermissionInContext(assigneeRoles, Context.Product, Permission.talentsManage);
        })
        .map(memberships => usersDict[getUuidFromIri(memberships.user)])
        .filter(item => !!item);

export const getTalentAssigneeData = (
    talentAssignees: User[],
    usersDict: Record<string, User>,
    currentUser: CurrentUser,
    currentAssignee?: Iri | null,
): UserPickerItemDataType<string>[] => {
    const usersOptions = talentAssignees.map((user): UserPickerItemDataType<string> => {
        const disabled = !user.activated || user.disabled;
        const labelDescription = disabled ? (user.activated && 'Disabled') || 'Inactive' : undefined;
        const userAllNames = `${user?.givenName ?? ''} ${user?.familyName ?? ''}`;
        const displayName = userAllNames.trim() ? userAllNames : user.email || user.phone;

        return {
            value: generateIri(ResourceKind.User, user.id),
            user: user,
            label: `${displayName} ${user.id === currentUser.user.id ? '(You)' : ''}`.trim(),
            avatarUrl: user.avatarUrl,
            disabled,
            labelDescription,
        };
    });

    if (currentAssignee) {
        const currentAssigneeUser = usersDict[getUuidFromIri(currentAssignee)] as User | undefined;

        const currentAssigneeOption = currentAssigneeUser
            ? usersOptions.find(
                  (userOption: Option) =>
                      getUuidFromNullableItem((userOption.value as any) ?? null) === currentAssigneeUser?.id,
              )
            : undefined;

        if (!currentAssigneeOption && currentAssigneeUser) {
            usersOptions.push({
                value: currentAssigneeUser.id,
                label: `${getUserDisplayName(currentAssigneeUser)} (unavailable)`,
                avatar: usersDict[currentAssigneeUser.id],
                disabled: true,
            });
        }
    }

    return [
        {
            value: UNASSIGNED_OPTION_VALUE,
            label: 'Unassigned',
            user: undefined,
        },
        ...usersOptions,
    ];
};

export const getTalentFullName = (talent: Talent | TalentEntry | null): string => {
    if (!talent) return '';
    return `${talent.firstName || ''} ${talent.lastName || ''}`.trim();
};

export type WidgetTalentEntry = Extract<
    TalentEntry,
    { talentSource: TalentSource.WebsiteWidget | TalentSource.WebChat }
>;

export const isWidgetTalentEntry = (talentEntry: TalentEntry): talentEntry is WidgetTalentEntry =>
    talentEntry.talentSource === TalentSource.WebsiteWidget || talentEntry.talentSource === TalentSource.WebChat;

type ParsedTalentEntryAdditionalData = Record<string, unknown>;

const parseTalentEntryAdditionalData = (additionalData: string | null): ParsedTalentEntryAdditionalData | null => {
    if (!additionalData) {
        return null;
    }

    try {
        const parsedData = JSON.parse(additionalData);

        return parsedData && typeof parsedData === 'object' ? (parsedData as ParsedTalentEntryAdditionalData) : null;
    } catch {
        return null;
    }
};

const getAdditionalDataString = (
    additionalData: ParsedTalentEntryAdditionalData | null,
    key: string,
): string | null => {
    const value = additionalData?.[key];

    return typeof value === 'string' && value.trim() ? value : null;
};

export const getWidgetTalentEntryPageData = (
    talentEntry: WidgetTalentEntry,
): { originPageTitle: string | null; originPageUrl: string | null } => {
    const parsedAdditionalData = parseTalentEntryAdditionalData(talentEntry.additionalData);

    return {
        originPageTitle: getAdditionalDataString(parsedAdditionalData, 'originPageTitle'),
        originPageUrl: talentEntry.originUrl,
    };
};

export const getTalentDisplayName = (talent: Talent): string => {
    if (talent.firstName || talent.lastName) {
        return getTalentFullName(talent);
    }
    if (talent.phone?.value) {
        return formatTalentPhoneNumber(talent.phone.value);
    }
    if (talent.email) {
        return talent.email;
    }
    if (talent.whatsappUsername) {
        return talent.whatsappUsername;
    }
    return 'talent';
};

export const getTalentDisplayNameWithEmailPriority = (talent: Talent): string => {
    if (talent.firstName || talent.lastName) {
        return getTalentFullName(talent);
    }
    if (talent.email) {
        return talent.email;
    }
    if (talent.whatsappUsername) {
        return talent.whatsappUsername;
    }
    if (talent.phone?.value) {
        return formatTalentPhoneNumber(talent.phone.value);
    }
    return 'talent';
};

const validateIsPhoneNumberUnique = (phoneNumber: string, allNumbers: string[]): any => {
    const isTheSame = allNumbers.some(num => {
        const matchLevel = phoneUtil.isNumberMatch(formatTalentPhoneNumber(phoneNumber), formatTalentPhoneNumber(num));
        return matchLevel === 4;
    });

    const isValid = !isTheSame;

    return isValid;
};

export const getAllPhoneNumbersWithoutCurrentTalentNumber = (
    currentTalentId: string,
    allTalents: (TalentEntry | Talent)[],
): string[] => {
    return allTalents
        ? (allTalents
              .filter(talent => talent.id !== currentTalentId && talent.phone !== null)
              .map(talent => talent.phone?.value) as string[])
        : [];
};

export const formatTalentPhoneNumber = (
    phoneNumber: string,
    phoneNumberFormat: PhoneNumberFormat = PhoneNumberFormat.INTERNATIONAL,
): string => {
    const trimmedPhoneNumber = phoneNumber.trim();
    try {
        const number = phoneUtil.parseAndKeepRawInput(trimmedPhoneNumber || '');
        return phoneUtil.format(number, phoneNumberFormat);
    } catch (e) {
        return trimmedPhoneNumber || '';
    }
};

const getRegionCodeFromPhoneNumber = (phoneNumber: string): CountryCode | null => {
    try {
        const parsedPhoneNumber = phoneUtil.parse(phoneNumber);
        return (phoneUtil.getRegionCodeForNumber(parsedPhoneNumber) ?? null) as CountryCode | null;
    } catch (error) {
        return null;
    }
};

const getCountryCodeFromPhoneNumber = (phoneNumber: string): number | null => {
    const regionCode = getRegionCodeFromPhoneNumber(phoneNumber);
    return regionCode && phoneUtil.getCountryCodeForRegion(regionCode);
};

export type CountryMetadata = {
    regionId: CountryCode;
    countryCode: string;
};

export const getCountryMetadataFromPhoneNumber = (phoneNumber: string): CountryMetadata | null => {
    const regionId = getRegionCodeFromPhoneNumber(phoneNumber);
    const countryCode = getCountryCodeFromPhoneNumber(phoneNumber);
    if (regionId && countryCode) {
        return {
            regionId,
            countryCode: `+${countryCode}`,
        };
    }
    return null;
};

export const NOT_UNIQUE_PHONE_NUMBER_MESSAGE = 'Phone number is assigned to another talent';
export const INVALID_PHONE_NUMBER_MESSAGE = 'Invalid phone number';
const SUPPORTED_REGION_IDS = phoneUtil.getSupportedRegions() as CountryCode[];
const PHONE_NUMBER_TO_LONG_MESSAGE = `Phone must be at most ${PHONE_NUMBER_MAX_LENGTH} characters length`;
export const NOT_UNIQUE_EMAIL_MESSAGE = 'Email is assigned to another talent';

export const SUPPORTED_REGIONS = SUPPORTED_REGION_IDS.map(regionId => ({
    regionId,
    countryCode: `+${phoneUtil.getCountryCodeForRegion(regionId)}`,
    name: CountryList[regionId],
}));

export const getTalentPhoneValidationSchemaForField = (
    allPhoneNumbersWithoutCurrentTalent: string[],
    businessAccountId: string,
    talentId?: string,
): Yup.AnySchema =>
    Yup.string()
        .trim()
        .test('is-valid', INVALID_PHONE_NUMBER_MESSAGE, value => {
            if (value) {
                return validatePhoneNumber(value);
            }
            return true;
        })
        .test('length', PHONE_NUMBER_TO_LONG_MESSAGE, value => (value?.length || 0) <= PHONE_NUMBER_MAX_LENGTH)
        .test('is-unique', NOT_UNIQUE_PHONE_NUMBER_MESSAGE, async value => {
            if (value) {
                const isUniqueSync = validateIsPhoneNumberUnique(value, allPhoneNumbersWithoutCurrentTalent);
                if (isUniqueSync && validatePhoneNumber(value)) {
                    const searchParams = new URLSearchParams('');
                    searchParams.set('phone', value);
                    const talentEntries = await withErrorHandler(async () =>
                        TalentGateway.fetchTalents(businessAccountId, searchParams),
                    );
                    const phoneNotUnique = talentEntries?.filter(talent => talent.id !== talentId).length;
                    return !phoneNotUnique;
                }
                return false;
            }
            return true;
        });

export const getTalentPhoneValidationSchema = (
    allPhoneNumbersWithoutCurrentTalent: string[],
    businessAccountId: string,
    talentId?: string,
): Yup.AnyObjectSchema =>
    Yup.object({
        phone: getTalentPhoneValidationSchemaForField(allPhoneNumbersWithoutCurrentTalent, businessAccountId, talentId),
    });

export const getTalentAssigneePlaceholderLetters = (assigneeFullName: string | undefined): string => {
    if (assigneeFullName) {
        const names = assigneeFullName.split(' ').filter(item => !!item);
        if (names.length > 1) {
            return names.shift()!.charAt(0) + names.pop()!.charAt(0);
        } else {
            return assigneeFullName.charAt(0);
        }
    }
    return '';
};

export const getTalentUniqueEmailValidationSchemaForField = (
    allEmailsWithoutCurrentTalent: string[],
    businessAccountId: string,
    currentTalentEmail?: string | null,
): Yup.AnySchema =>
    getTalentEmailValidationSchemaForField().test('is-unique', NOT_UNIQUE_EMAIL_MESSAGE, async value => {
        if (value && value !== currentTalentEmail) {
            const searchParams = new URLSearchParams('');
            searchParams.set('emailCanonical', value);
            const talentEntries = await withErrorHandler(async () =>
                TalentGateway.fetchTalents(businessAccountId, searchParams),
            );
            const emailNotUnique = talentEntries?.filter(talent => talent.email !== currentTalentEmail).length;
            return !emailNotUnique;
        }
        return true;
    });

export const getTalentEmailValidationSchemaForField = (): Yup.AnySchema =>
    Yup.string()
        .email()
        .test('length', EMAIL_TO_LONG_MESSAGE, value => (value?.length || 0) <= EMAIL_MAX_LENGTH);

export const getTalentEmailValidationSchema = (
    allEmailsWithoutCurrentTalent: string[],
    businessAccountId: string,
    currentTalentEmail: string | null,
): Yup.AnyObjectSchema =>
    Yup.object({
        email: getTalentUniqueEmailValidationSchemaForField(
            allEmailsWithoutCurrentTalent,
            businessAccountId,
            currentTalentEmail,
        ),
    });

type GoToConversationUrlParameter = 'goToConversation';
type GoToConversationUrlParameterValue = 'true';
export const GO_TO_CONVERSATION_URL_PARAMETER: GoToConversationUrlParameter = 'goToConversation';
export const GO_TO_CONVERSATION_URL_PARAMETER_VALUE: GoToConversationUrlParameterValue = 'true';

export const addGoToConversationUrlParam = (params?: string): string => {
    const param = new URLSearchParams(params ?? '');
    param.set(GO_TO_CONVERSATION_URL_PARAMETER, GO_TO_CONVERSATION_URL_PARAMETER_VALUE);
    return param.toString();
};

export const checkGoToConversationUrlParam = (locationSearch?: string): boolean => {
    const param = new URLSearchParams(locationSearch || window.location.search);
    return param.get(GO_TO_CONVERSATION_URL_PARAMETER) === GO_TO_CONVERSATION_URL_PARAMETER_VALUE;
};

export const removeGoToConversationUrlParam = (locationSearch: string, navigate: NavigateFunction): void => {
    const params = new URLSearchParams(locationSearch);
    params.delete(GO_TO_CONVERSATION_URL_PARAMETER);
    navigate({
        pathname: window.location.pathname,
        search: params.toString(),
        hash: window.location.hash,
    });
};

export const getNumberOfUnreadTalentConversationsForTabIndicator = (
    unreadConversationCounts: TalentsConversationsUnreadCountsByUser,
): number => unreadConversationCounts.primary;

export const getPushToAtsDisallowedReason = (komboIntegrations: KomboIntegration[]): string | undefined => {
    if (!komboIntegrations.length) {
        return 'ATS connection is required to push Talents';
    }

    const enabledIntegrations = komboIntegrations.filter(
        integration => integration.status === KomboIntegrationStatus.Enabled,
    );
    if (!enabledIntegrations.length) {
        return 'No enabled ATS connections available';
    }
};

export const getTalentAssigneeName = (
    assignee: string | null,
    talentAssigneeData: UserPickerItemDataType<string>[],
): string => {
    let currentAssigneeName = '';

    if (talentAssigneeData.length) {
        talentAssigneeData.some(currentAssignee => {
            if (currentAssignee?.user && assignee && assignee === currentAssignee.value) {
                currentAssigneeName = currentAssignee.user.givenName + ' ' + currentAssignee.user.familyName;
                return true;
            }
            return false;
        });
    }

    return currentAssigneeName;
};

export const extractIdFromAPIString = (apiString: string): string | undefined => apiString.split('/').pop();

export const getSelectedTalentStatusLabel = (
    statusId: string | null,
    talentStatuses: TalentStatus[],
): {
    theme: TalentStatusTheme;
    label: string;
} | null => {
    if (!statusId || !talentStatuses.length) {
        return null;
    }

    const selectedStatusId = extractIdFromAPIString(statusId);

    const status = talentStatuses.find(status => status.id === selectedStatusId);

    return status ? { theme: status.theme, label: status.label } : null;
};

export const getSelectedTalentpool = (
    talentpool: string | null,
    talentpoolsData: Talentpool[],
): Talentpool | undefined => {
    if (!talentpool || !talentpoolsData.length) {
        return undefined;
    }

    const selectedTalentpoolId = extractIdFromAPIString(talentpool);

    return talentpoolsData.find(talentpool => talentpool.id === selectedTalentpoolId);
};
